import RemoveIcon from '@mui/icons-material/Remove';
import { IconButton, Menu, Tooltip, Rating as StarRating, Button } from '@mui/material';
import { UserIcon } from 'components/CustomIcons';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import OrderWorkunit from 'types/entities/OrderWorkunit';
import User from 'types/entities/User';
import { MissionAdvancementPossibilities } from 'types/enums/MissionAdvancementPossibilities';
import { Rating as RatingType } from 'types/enums/Rating';
import Rating from './Rating';

type OrderWorkUnitSummaryRowType = {
  id: number;
  name: string;
  workunits: OrderWorkunit[];
  accountable_id: number;
  accountable: User;
  ref_order: string;
  ref_work: string;
  order_scope_id: number;
  rating: string;
};

type RateComponentProps = {
  row?: OrderWorkunit | OrderWorkUnitSummaryRowType;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setRating: (orderWorkunit: any, rate: RatingType) => Promise<void>;
  disabled?: boolean;
  isPartial?: boolean;
};

const RateComponent = (props: RateComponentProps) => {
  const { row, setRating, disabled, isPartial = false } = props;
  const [value, setValue] = React.useState<number | null>(0);
  const [hover, setHover] = React.useState(-1);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  function isOrderWorkunit(arg: OrderWorkunit | OrderWorkUnitSummaryRowType): arg is OrderWorkunit {
    return Object.keys(arg).includes('mission_advancement_id');
  }

  const emptyCell = <RemoveIcon color="disabled" />;
  const getStringToMap = () => {
    if (row && isOrderWorkunit(row)) {
      if (row.mission_advancement_id === MissionAdvancementPossibilities.REFUSED_CLIENT) return [RatingType.E];
      if (row?.mission_advancement_id && row.mission_advancement_id >= MissionAdvancementPossibilities.RESERVE_CLIENT)
        return [RatingType.S];
    }

    return isPartial ? [RatingType.A, RatingType.B, RatingType.C] : [RatingType.S];
  };

  const starLabels: { [index: string]: string } = {
    5: '5',
    4: '4',
    3: '3',
    2: '2',
    1: '1',
    0: '0',
  };

  function getStarLabelText(value: number) {
    return `${value} Star${value !== 1 ? 's' : ''}, ${starLabels[value]}`;
  }

  const starRating = {
    5: 5,
    4: 4,
    3: 3,
    2: 2,
    1: 1,
    0: 0,
    S: 'S',
  };

  const stringsToMap = getStringToMap();
  const renderButton = () => {
    if (row && row.rating && row.rating !== 'S') {
      return (
        <StarRating value={(starRating[row.rating as keyof typeof starRating] as any) ?? null} readOnly size="small" />
      );
    }
    if (row && row.rating === 'S') {
      return (
        <Button
          variant="contained"
          color="primary"
          size="small"
          style={{ padding: '1px 10px', backgroundColor: '#2465dd' }}
        >
          S
        </Button>
      );
    }
    if ((!row || !row.rating) && disabled) {
      return emptyCell;
    }
    return emptyCell;
  };
  const [t] = useTranslation();
  const [tooltipOpen, setTooltipOpen] = useState('');
  const handleTooltip = (rate: any) => {
    setTooltipOpen(rate);
  };

  const getTooltip = (rating: string) => {
    if (rating === '5') return t('5');
    if (rating === '4') return t('4');
    if (rating === '3') return t('3');
    if (rating === '2') return t('2');
    if (rating === '1') return t('1');
    if (rating === '0') return t('0');
    if (rating === 'S') return t('Skip');
    return '';
  };

  return (
    <div
      style={{
        position: 'relative',
        maxWidth: '100px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <Tooltip
        title={getTooltip(row?.rating as string) as string}
        key={`${row?.rating as string}`}
        open={tooltipOpen === (row?.rating as string)}
        placement="top"
        PopperProps={{ style: { pointerEvents: 'none', marginTop: '40px' } }}
      >
        <span
          // display:flex + lineHeight:0 strip the inline baseline leading this span would
          // otherwise add under the button, which pushed the reviewer badge down.
          style={{ display: 'flex', lineHeight: 0 }}
          onMouseEnter={() => {
            handleTooltip(row?.rating as string);
          }}
          onBlur={() => handleTooltip('')}
          onMouseLeave={() => handleTooltip('')}
        >
          <IconButton
            id="basic-button"
            aria-controls="basic-menu"
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
            onClick={handleClick}
            disabled={disabled}
            style={isPartial ? { border: '1px solid #dfd8d8', borderRadius: '0', marginLeft: '1rem' } : {}}
            size="large"
            color="primary"
            sx={{
              // Trim the large-size vertical padding so the reviewer badge below has room
              // to sit under the rating instead of on top of it.
              py: '4px',
              '&.Mui-disabled': {
                color: '#fff', // icon color
                opacity: 1, // prevent MUI dimming
              },
            }}
          >
            {renderButton()}
          </IconButton>
          <Menu
            id="basic-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            MenuListProps={{
              'aria-labelledby': 'basic-button',
            }}
            style={{ zIndex: 14 }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: '2px' }}>
              <div className="MuiRating-root" style={{ display: 'flex', alignItems: 'center' }}>
                <StarRating
                  name="hover-feedback"
                  value={value}
                  precision={1}
                  size="large"
                  getLabelText={getStarLabelText}
                  onChange={(event, newValue) => {
                    const rating = newValue ?? 0;
                    setValue(rating);
                    setAnchorEl(null);
                    setRating(row as OrderWorkunit | OrderWorkUnitSummaryRowType, rating.toString() as RatingType);
                  }}
                  onChangeActive={(event, newHover) => {
                    const hover = newHover ?? 0;
                    setHover(hover);
                  }}
                />
              </div>

              {value !== null && (
                <span
                  style={{
                    backgroundColor: '#2465dd',
                    color: '#fff',
                    fontSize: 15,
                    padding: 6,
                    borderRadius: 6,
                    marginLeft: 4,
                  }}
                >
                  {value}
                </span>
              )}

              {stringsToMap.map((rate, index) => (
                <IconButton
                  sx={{
                    '&.Mui-disabled': {
                      color: '#fff', // icon color
                      opacity: 1, // prevent MUI dimming
                    },
                  }}
                  disabled={disabled}
                  key={rate}
                  onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                    const rating = 0;
                    setRating(row as OrderWorkunit | OrderWorkUnitSummaryRowType, rating.toString() as RatingType);
                    handleClose();
                  }}
                  size="large"
                >
                  <Rating size="small" rating={rate} open={open} handleTooltipParent={handleTooltip} />
                </IconButton>
              ))}
            </div>
          </Menu>
        </span>
      </Tooltip>
      {row?.rating && (row as OrderWorkunit).reviewer && !(row as OrderWorkunit).reviewer?.is_client && (
        <Tooltip
          title={t('Assessed_Dm') as string}
          placement="top"
          arrow
          PopperProps={{
            sx: {
              '& .MuiTooltip-tooltip': {
                textAlign: 'center',
              },
            },
          }}
        >
          <div
            className="icon-container"
            style={{
              display: 'flex',
              justifyContent: 'center',
              lineHeight: 0,
              // The UserIcon glyph is inset ~2px at the top of its 20px box and sits
              // left of that box's centre, so nudge it up and right to tuck it neatly
              // under the rating.
              marginTop: -4,
              marginLeft: 2,
            }}
          >
            <UserIcon fontSize="small" />
          </div>
        </Tooltip>
      )}
    </div>
  );
};
// eslint-disable-next-line react-hooks/exhaustive-deps
export default (props: RateComponentProps) => useMemo(() => <RateComponent {...props} />, [props.row, props.disabled]);
